import { type ReactElement, type ReactNode, useState, useMemo } from 'react'
import {
  type HederaConnectedWallet,
  useConnectedHederaWallet,
  useHashPackRejectionError,
  clearHashPackRejectionError,
} from '@/hooks/wallets/useHashPack'
import useAccountResolver from '../AddressInput/useAccountResolver'
import { WalletContext } from './context'
import { getAddress, type Eip1193Provider } from 'ethers'
import { Alert, Snackbar } from '@mui/material'

const HederaWalletProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const wallet = useConnectedHederaWallet()
  const rejectionError = useHashPackRejectionError()
  const { address } = useAccountResolver(wallet?.address, wallet?.chainId)
  const [_, setSignerAddress] = useState<string>()

  const checksumAddress = useMemo(() => {
    if (!address) return ''
    return getAddress(address)
  }, [address])

  const signer = useMemo(() => {
    if (!wallet) return null

    const provider: Eip1193Provider = {
      request: () => Promise.resolve(),
    }

    return {
      provider,
      address: checksumAddress || wallet.address,
      chainId: wallet.chainId,
      isSafe: false,
    }
  }, [wallet, checksumAddress])

  const connectedWallet: HederaConnectedWallet | null = useMemo(() => {
    if (!wallet || !checksumAddress) return null

    return {
      label: wallet.label,
      chainId: wallet.chainId,
      address: checksumAddress,
      icon: wallet.icon,
      ens: wallet.address,
      provider: wallet.provider,
      balance: wallet.balance,
    }
  }, [wallet, checksumAddress])

  return (
    <WalletContext.Provider
      value={{
        connectedWallet,
        signer,
        setSignerAddress,
      }}
    >
      {children}
      <Snackbar
        open={!!rejectionError}
        autoHideDuration={15000}
        onClose={clearHashPackRejectionError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clearHashPackRejectionError} severity="error" variant="filled" sx={{ width: '100%' }}>
          {rejectionError}
        </Alert>
      </Snackbar>
    </WalletContext.Provider>
  )
}

export default HederaWalletProvider
